/**
 * A macro or nutrient amount, shown to as many decimals as it actually needs.
 *
 * Every portion of a food is the same panel scaled by weight, so the small ones carry small numbers:
 * a gram of banana holds 0.0109 g of protein and 0.0033 g of fat. Fixed to one decimal those both
 * render as "0", which made the 1 g portion look empty and made the portions look out of proportion
 * with each other when they are exactly proportional. Precision follows magnitude instead.
 */
export const formatAmount = (v) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return "0";
  const abs = Math.abs(n);
  const decimals = abs >= 1 ? 1 : abs >= 0.1 ? 2 : abs >= 0.01 ? 3 : 4;
  return n.toFixed(decimals).replace(/\.?0+$/, "");
};

/** Micronutrients often live on the LLM "100 g" anchor, not the user's default serving. */
export const formatNutrientUnit = (unit) => {
  const map = {
    mg: 'מ"ג',
    mcg: 'מק"ג',
    g: "גר'",
  };
  return map[unit?.toLowerCase()] || unit || "";
};

const isHundredGramAnchor = (serving) => {
  const n = (serving.name || "").replace(/\s/g, "").toLowerCase();
  return n.includes("100") && (n.includes("g") || n.includes("גרם") || n.includes("ml"));
};

const findMicronutrientAnchor = (servings) => {
  const withMicros = (servings || []).filter((s) => s.micronutrients?.length > 0);
  if (!withMicros.length) return null;
  return withMicros.find(isHundredGramAnchor)
    || withMicros.sort((a, b) => b.micronutrients.length - a.micronutrients.length)[0];
};

export const scaleMicronutrients = (sourceMicros, sourceGrams, targetGrams) => {
  if (!sourceMicros?.length || !sourceGrams || !targetGrams) return [];
  const factor = targetGrams / sourceGrams;
  return sourceMicros.map((n) => ({
    ...n,
    amount: n.amount != null ? n.amount * factor : null,
  }));
};

export const getServingMicronutrients = (food, serving) => {
  if (serving.micronutrients?.length) return serving.micronutrients;

  const anchor = findMicronutrientAnchor(food.servings);
  if (!anchor || anchor.id === serving.id) return [];

  return scaleMicronutrients(
    anchor.micronutrients,
    anchor.gramsPerServing,
    serving.gramsPerServing,
  );
};

/**
 * Derive a new portion's values from one that already exists, by weight.
 *
 * <p>Adding "1 piece = 30 g" to a food whose 100 g row already carries macros and a full
 * micronutrient panel is arithmetic, and arithmetic done by hand is arithmetic done wrong: the
 * macros get scaled and the twenty vitamin fields get left at zero, or a decimal slips. Everything
 * that has a number is scaled by the same factor, including micronutrients — which are pulled
 * through `getServingMicronutrients` so a base row that inherits its panel from the 100 g anchor
 * still passes it on.
 *
 * <p>Returns strings because the caller is a form; empty for anything the base did not have, so a
 * missing value stays missing rather than becoming a fabricated zero. Null when the scale factor is
 * not knowable — no base grams, no target grams — and the caller then leaves the form alone.
 */
export const scaleServingFrom = (food, sourceServing, targetGrams) => {
  const sourceGrams = Number(sourceServing?.gramsPerServing);
  const target = Number(targetGrams);
  if (!sourceGrams || !target || sourceGrams <= 0 || target <= 0) return null;

  const factor = target / sourceGrams;
  const nv = sourceServing.nutritionValues || {};

  return {
    // Significant digits, not decimal places, for the same reason the micronutrients below use them:
    // scaling 89 kcal down to a gram gives 0.89, and scaling 1.09 g of protein gives 0.0109. Rounding
    // either to a whole number or to one decimal writes a zero into the form.
    calories: nv.calories == null ? "" : String(significant(nv.calories * factor, 3)),
    protein: scaled(nv.protein, factor),
    carbs: scaled(nv.carbs, factor),
    fats: scaled(nv.fats, factor),
    micronutrients: getServingMicronutrients(food, sourceServing).reduce((acc, n) => {
      // Significant digits rather than decimal places: micronutrients span milligrams of calcium and
      // micrograms of B12, so a fixed 2 decimals would round the small ones to nothing while a fixed
      // 4 leaves 2.3333 on the large ones.
      if (n.amount != null) acc[n.code] = String(significant(n.amount * factor, 3));
      return acc;
    }, {}),
  };
};

/** 3 significant digits: 0.333333… → 0.333, 41.6666… → 41.7, 0.000123456 → 0.000123. */
const significant = (value, digits) => {
  if (!Number.isFinite(value) || value === 0) return 0;
  const magnitude = Math.ceil(Math.log10(Math.abs(value)));
  const decimals = Math.max(0, Math.min(20, digits - magnitude));
  return Number(value.toFixed(decimals));
};

const scaled = (value, factor) =>
  value == null ? "" : String(significant(Number(value) * factor, 3));

/**
 * Which portion the nutrition panel reads from when nobody has picked one.
 *
 * <p>Prefers a 100 g / 100 ml reference because that is the portion people compare foods on;
 * otherwise the food's own default, so a bread with no 100 g entry still shows "1 slice" rather than
 * nothing. Returns `{serving, basisLabel}` — `serving` is null only when the food has no portions at
 * all, in which case the caller falls back to the DTO's default-serving snapshot.
 */
export const resolveNutritionBasis = (food) => {
  const servings = food.servings || [];
  const isHundred = (s) =>
    s.gramsPerServing === 100 || isHundredGramAnchor(s);

  const hundred = servings.find(isHundred);
  if (hundred) return { serving: hundred, basisLabel: "ל-100 גרם" };

  const def = servings.find((s) => s.isDefault) || servings[0];
  if (def) return { serving: def, basisLabel: `למנה: ${def.name}` };

  return { serving: null, basisLabel: null };
};

export const getDisplayMicronutrients = (food) => {
  if (food.defaultServingMicronutrients?.length) {
    return { micronutrients: food.defaultServingMicronutrients, servingName: null };
  }

  const servings = food.servings || [];
  const defaultServing = servings.find((s) => s.isDefault) || servings[0];
  if (defaultServing) {
    const micros = getServingMicronutrients(food, defaultServing);
    if (micros.length) {
      return { micronutrients: micros, servingName: defaultServing.name };
    }
  }

  const withMicros = servings.filter((s) => s.micronutrients?.length > 0);
  if (!withMicros.length) {
    return { micronutrients: [], servingName: null };
  }

  const anchor = withMicros.find((s) => {
    const n = (s.name || "").replace(/\s/g, "").toLowerCase();
    return n.includes("100") && (n.includes("g") || n.includes("גרם") || n.includes("ml"));
  });

  const source = anchor || withMicros.sort((a, b) => b.micronutrients.length - a.micronutrients.length)[0];
  return { micronutrients: source.micronutrients, servingName: source.name };
};
